<template>
  <div>
    <el-form-item prop="mainImage" class="edit-form-item">
      <div class="item-name">
        <label for="mainImage">メイン画像</label>
      </div>
      <div class="image-upload-container" @click="handleImageUpload('main')">
        <input
          type="file"
          ref="mainImageInput"
          class="image-input"
          accept="image/*"
          @change="onImageSelected($event, 'main')"
          style="display: none"
        />
        <div class="upload-placeholder" v-if="!editableItem.mainImage">
          <i class="el-icon-picture"></i>
          <p>写真をアップロード</p>
        </div>
        <div v-else class="image-container">
          <img :src="editableItem.mainImage" class="preview-image" />
          <div class="delete-button" @click.stop="deleteImage('main')">
            <i class="el-icon-close"></i>
          </div>
        </div>
      </div>
    </el-form-item>

    <el-form-item prop="subImage" class="edit-form-item">
      <div class="item-name"><label for="subImage">サブ画像</label></div>
      <div class="sub-images-container">
        <div
          v-for="n in 5"
          :key="n"
          class="image-upload-container sub-image-item"
          @click="onSubSlotClick(n)"
        >
          <input
            type="file"
            :ref="(el) => setSubImageInput(n, el)"
            class="image-input"
            accept="image/*"
            @change="onImageSelected($event, `sub${n}`)"
            style="display: none"
          />
          <div class="upload-placeholder" v-if="!subImageAt(n)">
            <p class="lock-icon" v-if="!isYagiPrime">🔒️</p>
            <i class="el-icon-picture" v-else></i>
            <p>写真を<br class="sp" />アップロード</p>
          </div>
          <div v-else class="image-container">
            <img :src="subImageAt(n) || ''" class="preview-image" />
            <div class="delete-button" @click.stop="deleteImage(`sub${n}`)">
              <i class="el-icon-close"></i>
            </div>
          </div>
        </div>
      </div>
    </el-form-item>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type { ShokurekishoImageItem } from '@/composables/format-edit/useShokurekishoFormatData'

const props = defineProps<{
  item: ShokurekishoImageItem
}>()

const { vm, globalVar, yagiprimePaymentService, message } = useHostServices()
const isYagiPrime = computed(() => !!globalVar.isYagiPrime)
const editableItem = computed(() => props.item)

const mainImageInput = ref<HTMLInputElement | null>(null)
const subImageInputs: Record<number, HTMLInputElement | null> = {}

type FeatureHooks = {
  onSuccess: () => void
  onCancel: () => void
  onError?: () => void
}

function subImageAt(n: number): string | null {
  const key = `subImage${n}` as keyof ShokurekishoImageItem
  const value = editableItem.value[key]
  return typeof value === 'string' ? value : value || null
}

function setSubImageInput(n: number, el: unknown) {
  const node = Array.isArray(el) ? el[0] : el
  subImageInputs[n] = (node as HTMLInputElement) || null
}

function handleImageUpload(type: string) {
  if (type === 'main') {
    mainImageInput.value?.click()
    return
  }
  const n = Number(type.replace('sub', ''))
  subImageInputs[n]?.click()
}

function onSubSlotClick(n: number) {
  if (isYagiPrime.value) {
    handleImageUpload(`sub${n}`)
    return
  }
  yagiprimePaymentService.useFeature(
    vm,
    'shokurekisho-with-image-add-sub-image',
    {
      onSuccess: () => {
        message({
          message: '成功しました',
          type: 'success'
        })
      },
      onCancel: () => {},
      onError: () => {
        message({
          message: '失敗しました',
          type: 'error'
        })
      }
    } as FeatureHooks
  )
}

function setImageField(type: string, value: string | null) {
  if (type === 'main') {
    editableItem.value.mainImage = value
    return
  }
  const n = type.replace('sub', '')
  const key = `subImage${n}` as keyof ShokurekishoImageItem
  ;(editableItem.value as Record<string, string | null>)[key] = value
}

function onImageSelected(event: Event, type: string) {
  const input = event.target as HTMLInputElement
  const file = input.files && input.files[0]
  if (!file) return
  setImageField(type, null)
  const reader = new FileReader()
  reader.onload = (e) => {
    const result = e.target && e.target.result
    setImageField(type, typeof result === 'string' ? result : null)
  }
  reader.readAsDataURL(file)
}

function deleteImage(type: string) {
  setImageField(type, null)
}
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/variables';

.image-upload-container {
  width: 100%;
  height: 200px;
  border: 2px dashed #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.3s;
  position: relative;

  &:hover {
    border-color: #0f9934;
  }
}

.upload-placeholder {
  text-align: center;
  color: #909399;

  .lock-icon {
    font-size: 2rem;
  }

  i {
    font-size: 32px;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.sub-images-container {
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 10px;

  @include max-width(sm) {
    flex-wrap: wrap;
  }
}

.sub-image-item {
  height: 150px;

  @include max-width(sm) {
    width: calc(50% - 5px);
    height: 100px;
  }
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-button {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  background-color: rgb(0 0 0 / 50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;
  cursor: pointer;

  &:hover {
    background-color: rgb(0 0 0 / 70%);
  }

  i {
    font-size: 16px;
  }
}

.image-container:hover .delete-button {
  opacity: 1;
}
</style>
